import { Link } from '@tanstack/react-router';
import { brandOriginUrl } from '@/api';
import { casinoCategoryPath, casinoVendorLogoUrl, type CasinoVendor } from '@/domain';
import { cn } from '@/lib/cn';
import { Spinner } from '@/ui/common';

export interface ProvidersPanelProps {
  vendors: CasinoVendor[];
  category?: string;
  isLoading?: boolean;
  className?: string;
}

export function ProvidersPanel({
  vendors,
  category = 'slots',
  isLoading = false,
  className,
}: ProvidersPanelProps) {
  if (isLoading && vendors.length === 0) {
    return (
      <div className={cn('flex min-h-48 items-center justify-center', className)}>
        <Spinner label="Loading providers" />
      </div>
    );
  }

  if (vendors.length === 0) {
    return (
      <p className={cn('py-12 text-center text-body-s text-text-secondary', className)}>
        No providers available.
      </p>
    );
  }

  return (
    <div
      className={cn(
        'grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6',
        className,
      )}
    >
      {vendors.map((vendor) => (
        <Link
          key={vendor.Id}
          to={casinoCategoryPath(category, vendor.Name)}
          className="flex h-20 items-center justify-center rounded-md border border-border-default bg-bg-surface px-4 transition-colors hover:bg-bg-elevated"
          title={vendor.Name}
        >
          <img
            src={casinoVendorLogoUrl(brandOriginUrl(), vendor.Name)}
            alt={vendor.Name}
            className="max-h-10 max-w-full object-contain"
            onError={(event) => {
              event.currentTarget.style.display = 'none';
              const parent = event.currentTarget.parentElement;
              if (parent && !parent.querySelector('[data-vendor-fallback]')) {
                const span = document.createElement('span');
                span.dataset.vendorFallback = '1';
                span.className = 'text-label-s text-text-primary text-center';
                span.textContent = vendor.Name;
                parent.appendChild(span);
              }
            }}
          />
        </Link>
      ))}
    </div>
  );
}
